import { useMemo, useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { Link } from "react-router-dom"
import { fetchAnlagen, type Anlage } from "@/lib/api"
import { Badge, Input } from "@/components/ui"
import { DataGrid, type ColumnDef } from "@/components/DataGrid"

/** Anlagen-Übersicht mit Suche — Einstieg in die Hierarchie-Navigation (AP-3.3). */
export function AnlagenPage() {
  const [suche, setSuche] = useState("")

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["anlagen"],
    queryFn: fetchAnlagen,
  })

  // Die Suche deckt auch die TAG-Unit-Beschreibung ab, die keine Grid-Spalte ist —
  // deshalb bleibt sie zusätzlich zum Schnellfilter des Grids erhalten.
  const gefiltert = (data ?? []).filter((a) =>
    `${a.bezeichnung} ${a.tagUnitBeschreibung ?? ""} ${a.kostenstelleNavision ?? ""}`
      .toLowerCase()
      .includes(suche.toLowerCase()),
  )

  // Grid-Spalten: header als String, damit die Spalten-Auswahl ihn anzeigen kann
  const spalten = useMemo<ColumnDef<Anlage>[]>(
    () => [
      {
        id: "tagUnit",
        header: "TAG-Unit",
        accessorFn: (a) => (a.tagUnit !== null ? String(a.tagUnit).padStart(3, "0") : ""),
        cell: ({ row }) => (
          <Link to={`/maschinen/anlagen/${row.original.id}`} className="hover:underline">
            {row.original.tagUnit !== null ? String(row.original.tagUnit).padStart(3, "0") : "—"}
          </Link>
        ),
        meta: { cellClassName: "font-mono" },
      },
      {
        id: "bezeichnung",
        header: "Bezeichnung",
        accessorFn: (a) => a.bezeichnung,
        cell: ({ row }) => (
          <Link to={`/maschinen/anlagen/${row.original.id}`} className="hover:underline">
            {row.original.bezeichnung}
          </Link>
        ),
        meta: { cellClassName: "text-slate-900" },
      },
      {
        id: "kostenstelle",
        header: "Kostenstelle",
        accessorFn: (a) => a.kostenstelleNavision ?? "",
        cell: ({ row }) => row.original.kostenstelleNavision ?? "—",
      },
      {
        id: "prozessart",
        header: "Prozessart",
        accessorFn: (a) => a.prozessart ?? "",
        cell: ({ row }) => row.original.prozessart ?? "—",
      },
      {
        id: "status",
        header: "Status",
        accessorFn: (a) => (a.aktiv ? "aktiv" : "inaktiv"),
        cell: ({ row }) => (
          <Badge tone={row.original.aktiv ? "green" : "slate"}>
            {row.original.aktiv ? "aktiv" : "inaktiv"}
          </Badge>
        ),
      },
    ],
    [],
  )

  return (
    <div>
      <header className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Anlagen</h1>
          <p className="mt-1 text-sm text-slate-600">
            Kostenstellen/TAG-Units — Wurzel der Anlagen-/Objekt-/Komponentenhierarchie.
          </p>
        </div>
        <Link
          to="/maschinen/anlagen/neu"
          className="inline-flex h-9 items-center gap-2 rounded-md bg-slate-900 px-4 text-sm font-medium text-white transition-colors hover:bg-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
        >
          + Neue Anlage
        </Link>
      </header>

      <Input
        type="text"
        placeholder="Suche nach Bezeichnung/TAG-Unit/Kostenstelle…"
        value={suche}
        onChange={(e) => setSuche(e.target.value)}
        className="mb-4 w-80"
      />

      {isLoading && (
        <div className="animate-pulse space-y-2" aria-label="Lade Anlagen …">
          <div className="h-8 rounded-md bg-slate-100" />
          <div className="h-8 rounded-md bg-slate-100" />
          <div className="h-8 rounded-md bg-slate-100" />
        </div>
      )}
      {isError && (
        <p className="text-sm text-red-700">
          Anlagen konnten nicht geladen werden.{" "}
          <button
            type="button"
            onClick={() => refetch()}
            className="rounded-md underline hover:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
          >
            Erneut versuchen
          </button>
        </p>
      )}

      {data && (
        <DataGrid
          columns={spalten}
          data={gefiltert}
          storageKey="anlagen"
          emptyMessage={
            suche ? "Keine Anlagen für diese Suche gefunden." : "Noch keine Anlagen erfasst."
          }
        />
      )}
    </div>
  )
}
